import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ArrowRight, XCircle } from "lucide-react";

/* Dir 128.1 — reviewable NON-BIDDABLE notice-type gate. Shown when Anvil stops a pursuit because the notice
   looks non-biddable (Sources Sought / RFI, modification, sole-source NOI, award/special notice). Instead of a
   silent dead-end, it explains WHY and lets the operator either proceed anyway (audited override → re-runs the
   full pipeline, skipping ONLY this gate) or keep the NO-BID. Set-aside eligibility + past-due stay hard. */
export function NonBiddableGate({ dealId, reason, onDone }) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [note, setNote] = useState("");

  if (dismissed) return null;

  const proceed = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bids/${dealId}/notice-type/override`, { reason: note.trim() });
      toast.success(
        r?.data?.resumed
          ? "Proceeding anyway — Anvil is pursuing this as a bid."
          : "Override recorded."
      );
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not proceed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-5 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4 sm:p-5"
      data-testid="non-biddable-gate"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Anvil stopped — this doesn’t look like a biddable solicitation
          </h3>
          <p className="text-[13px] text-amber-800 dark:text-amber-300/90 mt-1.5 leading-relaxed" data-testid="non-biddable-reason">
            {reason ||
              "Anvil flagged this notice as non-competitive (e.g. a Sources Sought / RFI, modification, or sole-source notice) — there may be nothing to bid yet."}
          </p>
          <p className="text-xs text-amber-700/80 dark:text-amber-400/70 mt-2 leading-relaxed">
            If you know this is (or will become) a real bid, you can proceed anyway — Anvil will pursue it and
            stop again only if something is genuinely missing. It will never fabricate a price or proposal.
          </p>

          {showReason && (
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional: why are you proceeding? (recorded on the deal)"
              rows={2}
              data-testid="non-biddable-reason-input"
              className="mt-3 w-full text-[13px] px-3 py-2 rounded-md border border-amber-300 dark:border-amber-800 bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {!showReason ? (
              <button
                onClick={() => setShowReason(true)}
                disabled={busy}
                data-testid="non-biddable-proceed-btn"
                className="flex items-center gap-2 text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                <ArrowRight className="h-4 w-4" /> Proceed anyway
              </button>
            ) : (
              <button
                onClick={proceed}
                disabled={busy}
                data-testid="non-biddable-proceed-confirm-btn"
                className="flex items-center gap-2 text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Confirm — pursue as a bid
              </button>
            )}
            <button
              onClick={() => setDismissed(true)}
              disabled={busy}
              data-testid="non-biddable-keep-btn"
              className="flex items-center gap-2 text-xs font-semibold bg-white dark:bg-zinc-800 border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-4 py-2 rounded-md hover:bg-amber-100 dark:hover:bg-zinc-700 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" /> Keep as NO-BID
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
